/**
 * The two homepage price-drop rails (FRIDGE-1480).
 *
 * Owner, 10/12/2026, on a screenshot of "Fridge price drops" showing one Galanz deal three
 * times: "duplicates. also there should always be at least 10 in here. logic should be for
 * both fridge and freezer price drop component. first the ones with price drops, then the
 * ones that are the cheapest."
 *
 * So each rail is composed here, once, for the markup and the ItemList schema alike:
 *   1. the verified drops, in the order the deals derivation ranks them (biggest first);
 *   2. then the family's lowest tracked prices (`fridgeFill` / `freezerFill`, read cheapest
 *      first with the house rail rules: active, priced, in stock, a usable hero);
 *   3. then the family rail as a last resort, until the rail holds DROP_RAIL_MIN models;
 *   never the same FIN twice in a rail, and never a FIN the fridge rail already shows in the
 *   freezer rail.
 * The marquee itself (components/homepage-2026/marquee.tsx) only loops when a rail holds
 * MARQUEE_LOOP_MIN or more models, so a thin rail never shows a model twice either.
 */
import type { HomeDeal, HomeProduct, HomepageData } from './types'

/** The least a price-drop rail shows when the catalog can supply it. */
export const DROP_RAIL_MIN = 10
/** Below this many models the marquee renders one set and no loop copies. */
export const MARQUEE_LOOP_MIN = 6

export function composeDropRail(
  deals: readonly HomeDeal[],
  fill: readonly HomeProduct[],
  fallback: readonly HomeProduct[],
  exclude: ReadonlySet<string> = new Set(),
): HomeProduct[] {
  const out: HomeProduct[] = []
  const seen = new Set<string>(exclude)
  const take = (p: HomeProduct) => {
    if (seen.has(p.fin)) return
    seen.add(p.fin)
    out.push(p)
  }
  for (const d of deals) take(d.product)
  for (const p of [...fill, ...fallback]) {
    if (out.length >= DROP_RAIL_MIN) break
    take(p)
  }
  return out
}

export function dropRails(d: Pick<HomepageData, 'fridgeDeals' | 'freezerDeals' | 'fridgeFill' | 'freezerFill' | 'fridges' | 'freezers'>): { fridge: HomeProduct[]; freezer: HomeProduct[] } {
  const fridge = composeDropRail(d.fridgeDeals, d.fridgeFill, d.fridges)
  const freezer = composeDropRail(d.freezerDeals, d.freezerFill, d.freezers, new Set(fridge.map((p) => p.fin)))
  return { fridge, freezer }
}
